import { pick } from 'lodash-es';
import { getIdKey, getVertexId } from '../core/util';
import type { CoreTypes } from '../core';
import type { IdKey, InternalIdKey } from '../core/types/common';
import type { Types } from './types';
import type { PickEdges } from '../core/interfaces/vertices/util/common';
import type { Mixed } from '../core/interfaces/vertices/';
import type { Vertex, Collection } from '../core/interfaces/vertices/util/vertex';
import type { Coalesced } from '../core/interfaces/vertices/util/edges';
import type { DeepVertexExport } from '../core/interfaces/vertices/util';
import type { DivisionSettingsVertex } from '../core/interfaces/vertices/division-settings';
import type { LocationVertex } from '../core/interfaces/vertices/locations';
import type { GroupVertex } from '../core/interfaces/vertices/groups';
import type { TeacherVertex } from '../core/interfaces/vertices/teachers';
import type { PersonVertex } from '../core/interfaces/vertices/persons';
import type { PeriodVertex } from '../core/interfaces/vertices/periods';
import type { RootIntervalVertex } from '../core/interfaces/vertices/root-intervals';
import type { LockedTimeVertex } from '../core/interfaces/vertices/locked-times';
import type { LinkedEventsSetVertex } from '../core/interfaces/vertices/linked-events-sets';
import type { CourseVertex } from '../core/interfaces/vertices/courses';
import type { EventVertex } from '../core/interfaces/vertices/events';
import type { MixedVertexExport } from '../core/interfaces/vertices/util/deep-vertex-export';


type IgnoredEdges =
  | 'domain'
  | 'exceptions'
  | 'derivedParentGroups'
  | 'syllabus'
  | 'defaultLunch'
  | 'threads'
  | InternalIdKey;

type OmitProperties<T> = Omit<T, IgnoredEdges>;

type _Schedule = {
  division:         Mixed.Division;
  divisions:        Mixed.Division;
  settings:         Mixed.Settings;
  groups:           Mixed.Group[];
  teachers:         Mixed.Teacher[];
  locations:        Mixed.Location[];
  periods:          Mixed.Period[];
  persons:          Mixed.Person[];
  rootIntervals:    Mixed.RootInterval[];
  lockedTimes:      Mixed.LockedTime[];
  linkedEventsSets: Mixed.LinkedEventsSet[];
  courses:          Mixed.Course[];
  events:           Mixed.Event[];
};
export type MixedScheduleData = Partial<_Schedule>;

type Division         = Pick<CoreTypes.Deep.Division, 'displayName' | 'start' | 'end'>;
type DivisionSettings = CoreTypes.Deep.Settings;
type Location         = CoreTypes.Deep.Location;
type Group            = CoreTypes.Deep.Group;
type Teacher          = CoreTypes.Deep.Teacher;
type Person           = CoreTypes.Deep.Person;
type Period           = CoreTypes.Deep.Period;
type RootInterval     = CoreTypes.Deep.RootInterval;
type LockedTime       = CoreTypes.Deep.LockedTime;
type Course           = CoreTypes.Deep.Course;
type Event            = CoreTypes.Deep.Event;
type LinkedEventsSet  = CoreTypes.Deep.LinkedEventsSet;

export namespace ConnectedTypes {
  export type division         = OmitProperties<Division>;
  export type divisionSettings = OmitProperties<DivisionSettings>;
  export type location         = OmitProperties<Location>;
  export type group            = OmitProperties<Group>;
  export type teacher          = OmitProperties<Teacher>;
  export type person           = OmitProperties<Person>;
  export type period           = OmitProperties<Period>;
  export type rootInterval     = OmitProperties<RootInterval>;
  export type lockedTime       = OmitProperties<LockedTime>;
  export type course           = OmitProperties<Course>;
  export type event            = OmitProperties<Event>;
  export type linkedEventsSet  = OmitProperties<CoreTypes.Deep.LinkedEventsSet>;
}

export type ConnectedScheduleData = {
  division:         ConnectedTypes.division;
  settings:         ConnectedTypes.divisionSettings;
  groups:           ConnectedTypes.group[];
  teachers:         ConnectedTypes.teacher[];
  locations:        ConnectedTypes.location[];
  periods:          ConnectedTypes.period[];
  persons:          ConnectedTypes.person[];
  rootIntervals:    ConnectedTypes.rootInterval[];
  lockedTimes:      ConnectedTypes.lockedTime[];
  courses:          ConnectedTypes.course[];
  events:           ConnectedTypes.event[];
  linkedEventsSets: ConnectedTypes.linkedEventsSet[];
};

type DeepVertexEdgesOnlyExport<
  V extends Vertex<Collection>
> = {
  [K in keyof PickEdges<V> as K extends IgnoredEdges ? never : K]: DeepVertexExport<V>[K]
};


function indexCollection<T extends Vertex<Collection>> (collection: MixedVertexExport<T>[], idKey: IdKey): Map<string, DeepVertexExport<T>> {
  return new Map(collection.map(x => ([getVertexId(x, idKey), x as DeepVertexExport<T>] as const)));
}
function nonNullable<T> (value: T | null | undefined): value is T {
  return value != null;
}

/**
 * Connects the schedule data by replacing references with actual objects making it easier to work with.
 */
export function makeConnected (
  schedule: MixedScheduleData,
  options:  Pick<Types.toOptions, 'idKey'> = {}
): ConnectedScheduleData {
  const _division = schedule.divisions ?? schedule.division;
  if (!_division) throw new Error(`"division" or "divisions" missing in schedule, present keys: ${Object.keys(schedule).join(', ')}`);
  const _settings = schedule.settings;
  if (!_settings) throw new Error(`"settings" missing in schedule, present keys: ${Object.keys(schedule).join(', ')}`);

  const idKey = getIdKey(options);

  const groupsMap           = indexCollection<GroupVertex          >(schedule.groups           ?? [], idKey);
  const teachersMap         = indexCollection<TeacherVertex        >(schedule.teachers         ?? [], idKey);
  const locationsMap        = indexCollection<LocationVertex       >(schedule.locations        ?? [], idKey);
  const coursesMap          = indexCollection<CourseVertex         >(schedule.courses          ?? [], idKey);
  const eventsMap           = indexCollection<EventVertex          >(schedule.events           ?? [], idKey);
  const periodsMap          = indexCollection<PeriodVertex         >(schedule.periods          ?? [], idKey);
  const personsMap          = indexCollection<PersonVertex         >(schedule.persons          ?? [], idKey);
  const lockedTimesMap      = indexCollection<LockedTimeVertex     >(schedule.lockedTimes      ?? [], idKey);
  const rootIntervalsMap    = indexCollection<RootIntervalVertex   >(schedule.rootIntervals    ?? [], idKey);
  const linkedEventsSetsMap = indexCollection<LinkedEventsSetVertex>(schedule.linkedEventsSets ?? [], idKey);

  const division: Division = pick(_division, [ 'displayName', 'start', 'end']);

  const settings = ((): OmitProperties<DivisionSettings> => {
    const edges: DeepVertexEdgesOnlyExport<DivisionSettingsVertex> = {
      period:              _settings.period              != null ? periodsMap      .get(getVertexId(_settings.period,              idKey)) : _settings.period,
      defaultRootInterval: _settings.defaultRootInterval != null ? rootIntervalsMap.get(getVertexId(_settings.defaultRootInterval, idKey)) : _settings.defaultRootInterval,
    } satisfies Record<keyof DeepVertexEdgesOnlyExport<DivisionSettingsVertex>, unknown>;
    return Object.assign(_settings, edges);
  })();

  const getLockedTimes = <T extends Mixed.Group['lockedTimes'] | undefined | null>(x: T): LockedTime[] | Extract<T, null | undefined> => {
    if (!x) return x as Extract<T, null | undefined>;
    return x.map(x => lockedTimesMap.get(getVertexId(x, idKey))).filter(nonNullable);
  };
  const getLunch = <T extends Mixed.Group['lockedTimes'] | undefined | null>(x: T): LockedTime[] | Extract<T, null | undefined> => {
    if (!x) return x as Extract<T, null | undefined>;
    return x.map(x => lockedTimesMap.get(getVertexId(x, idKey))).filter(nonNullable);
  };
  const getComplementaryTimes = <T extends Mixed.Teacher['lockedTimes'] | undefined | null>(x: T): LockedTime[] | Extract<T, null | undefined> => {
    if (!x) return x as Extract<T, null | undefined>;
    return x.map(x => lockedTimesMap.get(getVertexId(x, idKey))).filter(nonNullable);
  };

  const locations = (schedule.locations ?? [])
    .map((x): OmitProperties<Location> => {

      const edges: DeepVertexEdgesOnlyExport<LocationVertex> = {
        lockedTimes: getLockedTimes(x.lockedTimes),
        lunch:       getLunch(x.lunch),
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<LocationVertex>, unknown>;

      return Object.assign(x, edges);
    });

  const groups = (schedule.groups ?? [])
    .map((x): OmitProperties<Group> => {
      const edges: DeepVertexEdgesOnlyExport<GroupVertex> = {
        lockedTimes:  getLockedTimes(x.lockedTimes),
        lunch:        getLunch      (x.lunch),
        members:      x.members      ? x.members     .map(x => personsMap.get(getVertexId(x, idKey))).filter(nonNullable) : x.members,
        parentGroups: x.parentGroups ? x.parentGroups.map(x => groupsMap .get(getVertexId(x, idKey))).filter(nonNullable) : x.parentGroups,
        subGroups:    x.subGroups    ? x.subGroups   .map(x => groupsMap .get(getVertexId(x, idKey))).filter(nonNullable) : x.subGroups,

        rootInterval: x.rootInterval != null ? rootIntervalsMap.get(getVertexId(x.rootInterval, idKey)) : x.rootInterval,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<GroupVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const teachers = (schedule.teachers ?? [])
    .map((x): OmitProperties<Teacher> => {
      const edges: DeepVertexEdgesOnlyExport<TeacherVertex> = {
        lockedTimes:        getLockedTimes       (x.lockedTimes),
        lunch:              getLunch             (x.lunch),
        complementaryTimes: getComplementaryTimes(x.complementaryTimes),

        person:       x.person       != null ? personsMap      .get(getVertexId(x.person,       idKey)) : x.person,
        rootInterval: x.rootInterval != null ? rootIntervalsMap.get(getVertexId(x.rootInterval, idKey)) : x.rootInterval,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<TeacherVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const persons = (schedule.persons ?? [])
    .map((x): OmitProperties<Person> => {
      const edges: DeepVertexEdgesOnlyExport<PersonVertex> = {
        group: x.group != null ? groupsMap.get(getVertexId(x.group, idKey)) : x.group,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<PersonVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const periods = (schedule.periods ?? [])
    .map((x): OmitProperties<Period> => {
      const edges: DeepVertexEdgesOnlyExport<PeriodVertex> = {
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<PeriodVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const rootIntervals = (schedule.rootIntervals ?? [])
    .map((x): OmitProperties<RootInterval> => {
      const edges: DeepVertexEdgesOnlyExport<RootIntervalVertex> = {
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<RootIntervalVertex>, unknown>;
      return Object.assign(x, edges);
    });

  type InferCollection<V extends DeepVertexExport<Vertex<Collection>>> = V extends DeepVertexExport<Vertex<infer C>> ? C : never;
  const makeCoalesced = <T extends DeepVertexExport<Vertex<Collection>>, C extends InferCollection<T>, E extends object> (
    map:    Map<string, T>,
    id:     string, toModel: C,
    extra?: E
  ) => {
    const to = map.get(id);
    if (!to) return;
    return { ...extra, to, toModel } as Coalesced.Util.Value<T, C, object, E>;
  };

  const lockedTimes = (schedule.lockedTimes ?? [])
    .map((x): OmitProperties<LockedTime> => {
      const edges: DeepVertexEdgesOnlyExport<LockedTimeVertex> = {
        coalesced: x.coalesced
          ? x.coalesced
            .map((x) => {
              const toModel = x.toModel;
              switch (toModel) {
                case 'groups':       return makeCoalesced(groupsMap,    getVertexId(x.to, idKey), toModel);
                case 'teachers':     return makeCoalesced(teachersMap,  getVertexId(x.to, idKey), toModel);
                case 'locations':    return makeCoalesced(locationsMap, getVertexId(x.to, idKey), toModel);
                case 'courses':      return makeCoalesced(coursesMap,   getVertexId(x.to, idKey), toModel);
                case 'events':       return makeCoalesced(eventsMap,    getVertexId(x.to, idKey), toModel);
                case 'courseevents': return makeCoalesced(eventsMap,    getVertexId(x.to, idKey), 'events');
                // case 'persons':      return makeCoalesced(personsMap,   getVertexId(x.to, idKey), x.toModel);
                default: throw new Error(`Unknown coalesced model "${toModel satisfies never as string}" in locked time ${JSON.stringify(x)}`);
              }
            })
            .filter(nonNullable)
          : x.coalesced,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<LockedTimeVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const getParticipants = (x: Mixed.Course['participants'] | Mixed.Event['participants']): CoreTypes.Deep.Course['participants'] | undefined => {
    if (!x) return undefined;
    return x
      .map(x => {
        const to = personsMap.get(getVertexId(x.to, idKey));
        if (!to) return;
        const participant: NonNullable<CoreTypes.Deep.Course['participants']>[number] = { ...x, to };
        return participant;
      })
      .filter(nonNullable);
  };
  const getTeachers = (x: Mixed.Course['teachers'] | Mixed.Event['teachers']): CoreTypes.Deep.Course['teachers'] | undefined => {
    if (!x) return undefined;
    return x
      .map(x => {
        const to = teachersMap.get(getVertexId(x.to, idKey));
        if (!to) return;
        const participant: NonNullable<CoreTypes.Deep.Course['teachers']>[number] = { ...x, to };
        return participant;
      })
      .filter(nonNullable);
  };
  const getLocations = (x: Mixed.Course['locations'] | Mixed.Event['locations']): CoreTypes.Deep.Course['locations'] | undefined => {
    if (!x) return undefined;
    return x
      .map(x => {
        const locations = x.locations
          .map(x => locationsMap.get(getVertexId(x, idKey)))
          .filter(nonNullable);
        if (locations.length != 1) throw new Error(`(RS::MakeConnected) Expected exactly one location in available locations, got ${locations.length} and before filtering: ${x.locations.map(x => getVertexId(x, idKey)).join(', ')}`);
        const availableLocation: NonNullable<CoreTypes.Deep.Course['locations']>[number] = { ...x, locations: locations as [Location] };
        return availableLocation;
      })
      .filter(nonNullable);
  };
  const getGroups = (x: Mixed.Course['groups'] | Mixed.Event['groups']): CoreTypes.Deep.Course['groups'] | undefined => {
    if (!x) return undefined;
    return x
      .map(x => {
        const to = groupsMap.get(getVertexId(x.to, idKey));
        if (!to) return;
        const exclude = x.exclude
          ? x.exclude
            .map(x => personsMap.get(getVertexId(x, idKey)))
            .filter(nonNullable)
          : undefined;
        const group: NonNullable<CoreTypes.Deep.Course['groups']>[number] = { ...x, to, exclude };
        return group;
      })
      .filter(nonNullable);
  };

  const courses = (schedule.courses ?? [])
    .map((x): OmitProperties<Course> => {
      const edges: DeepVertexEdgesOnlyExport<CourseVertex> = {
        groups:       getGroups      (x.groups),
        teachers:     getTeachers    (x.teachers),
        locations:    getLocations   (x.locations),
        participants: getParticipants(x.participants),
        lockedTimes:  getLockedTimes (x.lockedTimes),
        events: x.events ? x.events.map(x => eventsMap.get(getVertexId(x, idKey))).filter(nonNullable) : x.events,

        period: x.period != null ? periodsMap.get(getVertexId(x.period, idKey)) : x.period,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<CourseVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const events = (schedule.events ?? [])
    .map((x): OmitProperties<Event> => {
      const edges: DeepVertexEdgesOnlyExport<EventVertex> = {
        groups:       getGroups      (x.groups),
        teachers:     getTeachers    (x.teachers),
        locations:    getLocations   (x.locations),
        participants: getParticipants(x.participants),
        lockedTimes:  getLockedTimes (x.lockedTimes),
        inLocations:  x.inLocations
          ? x.inLocations
            .map(x => x === null ? null : locationsMap.get(getVertexId(x, idKey)))
            .filter((x): x is Exclude<typeof x, undefined> => x !== undefined)
          : x.inLocations,

        course:          x.course          != null ? coursesMap         .get(getVertexId(x.course,          idKey)) : x.course,
        period:          x.period          != null ? periodsMap         .get(getVertexId(x.period,          idKey)) : x.period,
        linkedEventsSet: x.linkedEventsSet != null ? linkedEventsSetsMap.get(getVertexId(x.linkedEventsSet, idKey)) : x.linkedEventsSet,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<EventVertex>, unknown>;
      return Object.assign(x, edges);
    });

  const linkedEventsSets = (schedule.linkedEventsSets ?? [])
    .map((x): OmitProperties<LinkedEventsSet> => {
      const edges: DeepVertexEdgesOnlyExport<LinkedEventsSetVertex> = {
        events: x.events
          ? x.events
            .map(x => eventsMap.get(getVertexId(x, idKey)))
            .filter(nonNullable)
          : x.events,
      } satisfies Record<keyof DeepVertexEdgesOnlyExport<LinkedEventsSetVertex>, unknown>;
      return Object.assign(x, edges);
    });


  return {
    division,
    settings,
    locations,
    groups,
    teachers,
    persons,
    periods,
    rootIntervals,
    lockedTimes,
    courses,
    events,
    linkedEventsSets
  };
}